import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, CircularProgress, IconButton, Tooltip
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Cell, LabelList
} from 'recharts';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosInstance from '../../../../config/axio.config';

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatCurrencyShort = (value) => {
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return value.toFixed(0);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5 }} elevation={3}>
      <Typography variant="body2" fontWeight="bold">{label}</Typography>
      {payload.map((entry, i) => (
        <Typography key={i} variant="body2" sx={{ color: entry.color }}>
          {entry.name}: {formatCurrency(entry.value)}
        </Typography>
      ))}
    </Paper>
  );
};

const SummaryCard = ({ title, value, icon, color, bgcolor }) => (
  <Paper
    elevation={2}
    sx={{
      p: 2.5,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      borderLeft: `4px solid ${color}`,
      height: '100%',
    }}
  >
    <Box sx={{
      bgcolor, borderRadius: '50%', width: 48, height: 48,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" noWrap>{title}</Typography>
      <Typography variant="h5" fontWeight="bold" sx={{ color }}>{formatCurrency(value)}</Typography>
    </Box>
  </Paper>
);

export default function FinanceiroPanelPage() {
  const [mesAno, setMesAno] = useState(dayjs());
  const [expenses, setExpenses] = useState(null);
  const [incomes, setIncomes] = useState(null);
  const [loading, setLoading] = useState(false);

  const periodo = useMemo(() => {
    const start = mesAno.startOf('month').format('YYYY-MM-DD');
    const end = mesAno.endOf('month').format('YYYY-MM-DD');
    return { dataInicio: start, dataFim: end };
  }, [mesAno]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [expRes, incRes] = await Promise.all([
        axiosInstance.get('/financeiro/expenses', { params: periodo }),
        axiosInstance.get('/financeiro/incomes', { params: periodo }),
      ]);
      setExpenses(expRes.data);
      setIncomes(incRes.data);
    } catch (err) {
      console.error('Erro ao buscar dados financeiros', err);
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalDespesas = expenses?.grandTotal || 0;
  const totalReceitas = incomes?.grandTotal || 0;
  const saldo = totalReceitas - totalDespesas;

  const comparisonData = useMemo(() => [
    { name: 'Receitas', valor: totalReceitas },
    { name: 'Despesas', valor: totalDespesas },
  ], [totalDespesas, totalReceitas]);

  const mesLabel = mesAno.locale('pt-br').format('MMMM [de] YYYY');

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight="bold">
          Painel Financeiro
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
            <DatePicker
              views={['month', 'year']}
              label="Mes/Ano"
              value={mesAno}
              onChange={(v) => v && setMesAno(v)}
              slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
            />
          </LocalizationProvider>
          <Tooltip title="Atualizar">
            <IconButton onClick={fetchData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <SummaryCard
                title="Total Despesas"
                value={totalDespesas}
                color="#ef5350"
                bgcolor="#ffebee"
                icon={<TrendingDownIcon sx={{ color: '#ef5350' }} />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SummaryCard
                title="Total Receitas"
                value={totalReceitas}
                color="#66bb6a"
                bgcolor="#e8f5e9"
                icon={<TrendingUpIcon sx={{ color: '#66bb6a' }} />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SummaryCard
                title="Saldo"
                value={saldo}
                color={saldo >= 0 ? '#42a5f5' : '#ef5350'}
                bgcolor={saldo >= 0 ? '#e3f2fd' : '#ffebee'}
                icon={<AccountBalanceIcon sx={{ color: saldo >= 0 ? '#42a5f5' : '#ef5350' }} />}
              />
            </Grid>
          </Grid>

          {/* Comparison: Despesas x Receitas */}
          <Paper sx={{ p: 2, mb: 2, height: 500 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom align="center">
              Receitas x Despesas - {mesLabel}
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={comparisonData} barSize={80}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(v) => formatCurrencyShort(v)} />
                <RTooltip content={<CustomTooltip />} />
                <Bar dataKey="valor" name="Valor" radius={[6, 6, 0, 0]}>
                  <Cell fill="#66bb6a" />
                  <Cell fill="#ef5350" />
                  <LabelList dataKey="valor" position="top" formatter={formatCurrency} style={{ fontSize: 13, fontWeight: 'bold' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>

        </>
      )}
    </Box>
  );
}
